// ─────────────────────────────────────────────────────────────────────────────
// IntegrationsPage, the public directory of what Wavelength actually reads.
//
// The page is built as three labelled tiers rather than one flattering grid,
// because the honesty is the design: connected today, works alongside, and on
// the roadmap. A reader should be able to tell, in one pass, which list a name
// sits in, and nothing has been moved up a list to make the page look fuller.
//
// The layout is a bordered directory at 16px radius rather than a card grid: a
// directory reads better as rows, one line per source, with the way it connects
// carried in a pill on the right. Roadmap rows use the kit's dashed muted pill,
// so "not built" is visible without reading a word.
//
// There is one <MediaSlot> on the page, captioned with the screenshot that
// belongs there. No photography exists yet.
//
// Claims removed deliberately: no card processor other than Datacap, no live
// bank feed, no accounting API, no register connectors. Names of registers
// appear only in the tier that says plainly they are not shipped connectors.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    BTN_ON_DARK,
    BTN_ON_DARK_SOFT,
    Icon,
    Container,
    Section,
    Heading,
    Lead,
    Body,
    Eyebrow,
    Pill,
    ChipRow,
    GlyphTile,
    MediaSlot,
    SectionHead,
    FeatureRow,
    Steps,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
    SITE_URL,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── Tier one: live in the product today ──────────────────────────────────────
const CONNECTED = [
    {
        icon: 'cart',
        name: 'WooCommerce',
        tag: 'Read only',
        body: 'Orders, catalogue and price history sync from your online store, so web orders sit in the same tables as counter sales with the same statuses and tenders. Wavelength reads the store and never writes back to it.',
    },
    {
        icon: 'wallet',
        name: 'Datacap processor reporting',
        tag: 'Card settlement',
        body: 'Card transactions and settlement batches per store, so what the registers rang can be matched against what the processor actually paid out. This is the one payment processor connected directly.',
    },
    {
        icon: 'chart',
        name: 'Google Analytics 4',
        tag: 'Campaigns',
        body: 'Campaign traffic merged with your real orders, so a campaign is judged on the revenue it produced rather than on sessions. Run dates can be marked on the trend and ranked against each other.',
    },
    {
        icon: 'receipt',
        name: 'QuickBooks Online',
        tag: 'CSV export',
        body: 'The closed day leaves as a journal-entry file your bookkeeper imports, with tax on its own line. It is an export, not an API connection, and Wavelength never writes into your books.',
    },
    {
        icon: 'bank',
        name: 'Bank statements',
        tag: 'CSV import',
        body: 'Your bank statement is uploaded as CSV and matched against sales, payouts and recorded deposits, with duplicate rows rejected on the way in. There is no live bank feed.',
    },
    {
        icon: 'lock',
        name: 'Amazon Cognito',
        tag: 'Sign in',
        body: 'Accounts, sessions and password rules for your workspace, with role-based access and view-as applied on top of them.',
    },
    {
        icon: 'pin',
        name: 'Google Maps',
        tag: 'Delivery tracking',
        body: 'The delivery map: where the driver is while a local run is out, and which stop the order belongs to.',
    },
    {
        icon: 'phone',
        name: 'Jamf Now',
        tag: 'iPad enrolment',
        body: 'Enrolment and configuration for the iPads on the floor, so a replacement device comes up already set up rather than being built by hand.',
    },
    {
        icon: 'layers',
        name: 'The five iPad apps',
        tag: 'Five apps',
        body: 'Register, Scale, Packing, Receiving and Delivery write straight into the same workspace, so what happens on the floor is in the numbers before anyone opens a laptop.',
    },
];

// ── Tier two: no connector, but the export is enough ─────────────────────────
const ALONGSIDE = [
    {
        icon: 'desktop',
        name: 'Any register that can report its sales',
        tag: 'Export based',
        body: 'If it can produce sales by day, location and tender, it can feed the close and the reconciliation. Operators run Wavelength on top of Toast, Clover, Square, Lightspeed and Shopify exactly this way.',
    },
    {
        icon: 'boxes',
        name: 'Any online store that exports orders',
        tag: 'Export based',
        body: 'A storefront other than WooCommerce arrives the same way. Orders, tenders and statuses are mapped once, then imported on the same schedule as everything else.',
    },
    {
        icon: 'bank',
        name: 'Any bank that exports a statement',
        tag: 'CSV import',
        body: 'Column layouts differ by bank. Yours is mapped one time, and every statement after that imports on that map, with duplicates rejected.',
    },
    {
        icon: 'cash',
        name: 'Any processor with a settlement report',
        tag: 'Report based',
        body: 'If the processor can produce batch and payout detail, those payouts can be matched against sales and deposits without a direct integration.',
    },
    {
        icon: 'search',
        name: 'Anything we have not seen yet',
        tag: 'By request',
        body: 'Send a sample file. Mapping a new source is normal work rather than a special project, and we will tell you plainly how long it takes.',
    },
];

// ── Tier three: not built, listed so nobody has to guess ─────────────────────
const ROADMAP = [
    {
        icon: 'desktop',
        name: 'Direct register connectors',
        tag: 'Not built',
        body: 'Reading sales from the register itself rather than from an export it produces. Today every register other than our own arrives as a file.',
    },
    {
        icon: 'bank',
        name: 'Live bank feed',
        tag: 'Not built',
        body: 'Statement activity arriving on its own instead of a CSV somebody uploads. The import is the only path today.',
    },
    {
        icon: 'receipt',
        name: 'Posting into the accounting ledger',
        tag: 'Not built',
        body: 'Writing the journal entry into the books over an API instead of handing across a file to import.',
    },
    {
        icon: 'wallet',
        name: 'More processors connected directly',
        tag: 'Not built',
        body: 'One processor is connected today. Everything else is reconciled from a settlement report until that changes.',
    },
    {
        icon: 'bolt',
        name: 'Public API and webhooks',
        tag: 'Not built',
        body: 'There is no public API. Data leaves through CSV, Excel and branded PDF export from any table in the product.',
    },
    {
        icon: 'chart',
        name: 'Cost of goods and margin',
        tag: 'Not built',
        body: 'Wavelength does not track cost of goods, so it does not report margin or profit. Revenue, mix, movement and variance are what it measures.',
    },
    {
        icon: 'shield',
        name: 'Single sign-on',
        tag: 'Not built',
        body: 'Signing in today is an email address and a password on your own workspace, with roles and permissions applied on top.',
    },
];

// ── The directory: bordered rows at 16px, one source per line ────────────────
// A grid of cards would flatten the three tiers into one impression. Rows keep
// the reading order, and the pill on the right says how each one connects.
function Directory({ items, meta = 'How it connects', muted = false, className = '' }) {
    return (
        <div className={`overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white ${className}`}>
            <div className="flex items-center justify-between gap-6 border-b border-[rgba(10,10,10,0.08)] bg-[#FAFAFA] px-6 py-3 sm:px-7">
                <span className="text-[12px] font-medium text-[#A1A1A1]">Source</span>
                <span className="text-[12px] font-medium text-[#A1A1A1]">{meta}</span>
            </div>
            {items.map((it, i) => (
                <div
                    key={it.name}
                    className={`flex flex-col gap-4 px-6 py-6 sm:flex-row sm:items-start sm:justify-between sm:gap-10 sm:px-7 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}
                >
                    <div className="flex items-start gap-4">
                        <div className="flex-none">
                            <GlyphTile name={it.icon} />
                        </div>
                        <div className="max-w-[600px]">
                            <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{it.name}</div>
                            <Body className="mt-1.5">{it.body}</Body>
                        </div>
                    </div>
                    <div className="flex-none pl-14 sm:pl-0 sm:pt-1">
                        <Pill muted={muted}>{it.tag}</Pill>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function IntegrationsPage() {
    return (
        <MarketingShell active="integrations">
            <Seo
                title="Integrations and the systems Wavelength reads"
                description="Wavelength integrations in three lists: WooCommerce, processor reporting, analytics and QuickBooks export today, plus what it reads alongside your register."
                path="/integrations"
                keywords="retail back office integrations, WooCommerce order sync, payment processor settlement reporting, bank statement CSV import, QuickBooks journal entry export, GA4 campaign attribution, POS agnostic back office, works with the register you run"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': `${SITE_URL}/integrations#page`,
                            url: `${SITE_URL}/integrations`,
                            name: 'Integrations and the systems Wavelength reads',
                            description: 'A directory of what Wavelength connects to today, what it works alongside by reading exports, and what is still on the roadmap.',
                            publisher: { '@id': `${SITE_URL}/#organization` },
                            breadcrumb: { '@id': `${SITE_URL}/integrations#breadcrumb` },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            '@id': `${SITE_URL}/integrations#breadcrumb`,
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/overview` },
                                { '@type': 'ListItem', position: 2, name: 'Integrations', item: `${SITE_URL}/integrations` },
                            ],
                        },
                        {
                            '@type': 'ItemList',
                            '@id': `${SITE_URL}/integrations#connected`,
                            name: 'Systems Wavelength is connected to today',
                            description: 'Sources Wavelength reads in the product today, each one live rather than planned.',
                            itemListOrder: 'https://schema.org/ItemListUnordered',
                            numberOfItems: CONNECTED.length,
                            itemListElement: CONNECTED.map((c, i) => ({
                                '@type': 'ListItem',
                                position: i + 1,
                                name: c.name,
                                description: c.body,
                            })),
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Integrations"
                title="What Wavelength reads, and what it does not."
                lead="Three lists, in plain language. What is connected today, what Wavelength works alongside by reading the exports your systems already produce, and what is still on the roadmap. Nothing has been moved up a list to make the page look better."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <Reveal>
                    <div className="flex flex-wrap gap-2">
                        <Pill as={Link} to="#connected">Connected today · {CONNECTED.length}</Pill>
                        <Pill as={Link} to="#alongside">Works alongside · {ALONGSIDE.length}</Pill>
                        <Pill as={Link} to="#roadmap" muted>On the roadmap · {ROADMAP.length}</Pill>
                    </div>
                </Reveal>
            </PageHero>

            {/* ── Tier one: connected today ──────────────────────────────── */}
            <Section id="connected" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Tier one"
                            title="Connected today."
                            lead="Every row below is live in the product now. Where a connection is import based rather than an interface between two systems, the row says so instead of hiding it behind the word integration."
                            actions={<Link to="/features" className={BTN_SECONDARY}>Everything in the platform<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <Directory className="mt-12" items={CONNECTED} />
                    </Reveal>
                    <Reveal delay={120}>
                        <Body className="mt-4">
                            Card settlement runs through one processor. Bank data arrives as a statement you import. Your books receive a file, not a posting.
                        </Body>
                    </Reveal>
                </Container>
            </Section>

            {/* ── How a connection behaves ───────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="How a connection behaves"
                            title="Read what is there. Write nothing back."
                            body="A connection is a read. Wavelength pulls what your systems already recorded, keeps its own corrections in a separate layer, and leaves the source exactly as it found it."
                            bullets={[
                                'Pulls are read only, so your store, your processor and your bank keep the original record',
                                'Corrections made here land in an overrides layer, never on the source data',
                                'Every match, every mismatch and every manual change is written to the audit log',
                                'A partial or timed-out pull is retried rather than allowed to replace a fuller set',
                            ]}
                            cta={<Link to="/security" className={BTN_SECONDARY}>How it is built<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Sources panel with the last read on every connection"
                                    note="Laptop screenshot: one row per source, each showing when it last read successfully and how many records came across"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Tier two: works alongside ──────────────────────────────── */}
            <Section id="alongside" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Tier two"
                            title="Works alongside the register you already run."
                            lead="There is no direct connector to the systems in this tier. Wavelength reads the sales export the system already produces, and new sources are added on request. Keeping the register you have is the point, not a compromise."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <Directory className="mt-12" items={ALONGSIDE} meta="How it is read" />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-8 flex flex-col gap-3">
                            <Eyebrow>Registers operators run underneath Wavelength</Eyebrow>
                            <ChipRow items={['Toast', 'Clover', 'Square', 'Lightspeed', 'Shopify']} />
                            <Body className="max-w-[560px]">
                                These are named because operators run them, not because they ship as connectors. Their sales reach Wavelength as an export, the same as any other register.
                            </Body>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Dark panel: keep the register ──────────────────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
                                <div className="flex flex-col">
                                    <h2 className="max-w-[460px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        Keep your point of sale. We reconcile it.
                                    </h2>
                                    <p className="mt-5 max-w-[460px] text-[16px] leading-[1.6] text-white/60">
                                        Replacing the register is the most expensive way to fix the back office, and it fixes the wrong half. The registers keep ringing sales. Wavelength takes what they recorded, matches it against the processor and the bank, and hands you the difference.
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        <Link to="/how-it-works" className={BTN_ON_DARK}>See how it works</Link>
                                        <Link to="/pricing" className={BTN_ON_DARK_SOFT}>See pricing</Link>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-center gap-3">
                                    <span className="text-[13px] text-white/45">What lands on top of your register</span>
                                    <div className="flex flex-wrap gap-2">
                                        {['Daily close', 'Blind counts', 'Processor batches', 'Bank statements', 'Exceptions queue', 'Audit log', 'Wholesale invoicing'].map((c, i) => (
                                            <Pill key={c} dark active={i === 0}>{c}</Pill>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Tier three: roadmap ────────────────────────────────────── */}
            <Section id="roadmap" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Tier three"
                            title="On the roadmap."
                            lead="None of the following is built. It is listed so nobody has to guess what the word integration is covering elsewhere on this page. No dates are attached to any of it, and none of it is sold as though it exists."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <Directory className="mt-12" items={ROADMAP} meta="Status" muted />
                    </Reveal>
                    <Reveal delay={120}>
                        <Body className="mt-4">
                            If one of these decides whether Wavelength is worth a call, say so and we will tell you where it sits rather than promise it.
                        </Body>
                    </Reveal>
                </Container>
            </Section>

            {/* ── What connecting involves ───────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>What connecting a source involves.</Heading>
                                <Lead>Most of the work is agreeing what a day is. The rest is mapping columns once, then letting it repeat.</Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_SECONDARY}>Ask about your systems<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Steps
                                items={[
                                    { title: 'Say what you run', body: 'Registers, online store, processor and bank, store by store. We tell you which of them is connected today and which arrives as an export.' },
                                    { title: 'Map each source once', body: 'Columns, locations and tenders are matched to your workspace. A bank layout or an export format is mapped one time and then repeats on its own.' },
                                    { title: 'Backfill and check', body: 'History loads behind you and is checked against a period you already closed by hand, so you watch the numbers agree before you rely on them.' },
                                    { title: 'Close a day on it', body: 'Run one real close on the connected sources. Anything that does not tie shows up in the exceptions queue with the location, the source, the date and the amount.' },
                                    { title: 'Add the next source', body: 'Bring in the other locations, the scales, receiving and wholesale when you are ready. Nothing has to land at once.' },
                                ]}
                            />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Integrations"
                title="Tell us what you run."
                body="Send the list of registers, processors and banks behind your counters. We will tell you which are connected today, which arrive as an export, and which are not built."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
